// Pomocnicze funkcje, stałe i komponenty używane WSPÓLNIE przez panel
// kierowcy i panel admina — wydzielone do osobnego pliku, żeby oba
// mogły z nich korzystać niezależnie od tego, czy AdminScreen jest akurat
// załadowany, czy nie (patrz code-splitting w App.jsx).
import { useState, useEffect, useLayoutEffect, useRef, useMemo } from "react";
import { lazy, Suspense } from "react";
import { MediaAttachment } from "./attach-view.jsx";
import { createPortal } from "react-dom";
import { AlertTriangle, CalendarClock, Check, CheckCheck, CheckCircle2, ChevronDown, ChevronLeft, Clock, Loader2, Mic, Paperclip, Pause, Play, Smile, Volume2, X } from "lucide-react";

export const COMPENSATION_REQUESTS_KEY = "fleet-compensation-requests-v1";

export function sameName(a, b) {
  return String(a || "").trim().toLowerCase() === String(b || "").trim().toLowerCase();
}

export function groupDisplayName(fullName, allDriverNames) {
  const parts = String(fullName || "").trim().split(/\s+/);
  const first = parts[0] || fullName;
  const last = parts.slice(1).join(" ");
  const sameFirstNameCount = (allDriverNames || []).filter(
    (n) => String(n || "").trim().split(/\s+/)[0].toLowerCase() === first.toLowerCase()
  ).length;
  if (sameFirstNameCount > 1 && last) return `${first} ${last[0]}.`;
  return first;
}

export const AVATAR_COLORS = ["#ff6b6b", "#ffa630", "#6ee7b7", "#60a5fa", "#c084fc", "#f472b6", "#38bdf8", "#fb923c"];

export function avatarColorFor(name) {
  const s = String(name || "");
  let hash = 0;
  for (let i = 0; i < s.length; i++) hash = (hash * 31 + s.charCodeAt(i)) >>> 0;
  return AVATAR_COLORS[hash % AVATAR_COLORS.length];
}

export function initials(name) {
  return String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0] || "")
    .join("")
    .toUpperCase();
}

export function formatPLN(n) {
  const safe = isNaN(n) ? 0 : n;
  // Zapobiega wyświetleniu "-0,00 zł" (np. dług spłacony co do grosza) —
  // matematycznie to "zero", ale JavaScript potrafi zostawić "ujemne
  // zero" po odejmowaniu/zaokrąglaniu, co wygląda jak błąd.
  return (safe === 0 ? 0 : safe).toLocaleString("pl-PL", {
    style: "currency",
    currency: "PLN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export async function sendNtfyNotification(title, message) {
  // Runs on our own server now (see /api/notify in server.js) — a plain
  // same-origin fetch, no browser sandbox restrictions apply here.
  try {
    await fetch("/api/notify", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ title, message }),
    });
  } catch (e) {
    /* best effort — the in-app copy fallback still covers this */
  }
}

export function liveCompAdjustment(compensationRequests, driverName, periodFrom, periodTo) {
  return (compensationRequests || [])
    .filter(
      (r) =>
        r.status === "approved" &&
        sameName(r.driverName, driverName) &&
        r.periodFrom === periodFrom &&
        r.periodTo === periodTo
    )
    .reduce((sum, r) => sum + (r.type === "add" ? r.amount : -r.amount), 0);
}

export const EMOJI_LIST = [
  "😀", "😂", "😍", "🙂", "😉", "😢", "😡", "🤔",
  "👍", "👎", "🙏", "👏", "💪", "🤝", "🎉", "🔥",
  "❤️", "⭐", "✅", "❌", "⏰", "📞", "🚗", "💰",
];

export async function compressImageFile(file, maxDimension = 1600, quality = 0.82) {
  if (file.type === "image/gif") return file;
  // PNG (i inne formaty z przezroczystością, np. WebP) NIE mogą być
  // zamieniane na JPEG — JPEG nie obsługuje przezroczystości, więc
  // canvas wypełniał przezroczyste tło na czarno przy eksporcie. Loga
  // firm bardzo często mają przezroczyste tło, więc to musi zostać
  // zachowane — eksportujemy z powrotem do PNG, nie JPEG.
  const preserveTransparency = file.type === "image/png" || file.type === "image/webp";
  try {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    if (width <= maxDimension && height <= maxDimension && file.size < 1_500_000) {
      if (bitmap.close) bitmap.close();
      return file; // już wystarczająco małe — nie ma co przerabiać
    }
    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const targetW = Math.round(width * scale);
    const targetH = Math.round(height * scale);
    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, 0, 0, targetW, targetH);
    if (bitmap.close) bitmap.close();
    const outType = preserveTransparency ? "image/png" : "image/jpeg";
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, outType, preserveTransparency ? undefined : quality));
    if (!blob || blob.size >= file.size) return file; // kompresja się nie opłaciła — zostaw oryginał
    const newExt = preserveTransparency ? "png" : "jpg";
    const newName = file.name.replace(/\.[^.]+$/, "") + "." + newExt;
    return new File([blob], newName, { type: outType });
  } catch (e) {
    return file;
  }
}

export async function uploadAttachment(file, onProgress) {
  const toUpload = (file.type || "").startsWith("image/") ? await compressImageFile(file) : file;
  // XHR z surowym plikiem jako treścią zapytania — bez zamiany na base64
  // (to dokładało ~33% do rozmiaru i długo obciążało telefon PRZED
  // rozpoczęciem wysyłki) i bez owijania w JSON. Dzięki temu wysyłka
  // zaczyna się od razu, a nie po kilku sekundach "mielenia" pliku.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const encodedName = encodeURIComponent(toUpload.name || "plik");
    xhr.open("POST", `/api/upload-attachment-binary?filename=${encodedName}`);
    xhr.setRequestHeader("Content-Type", toUpload.type || "application/octet-stream");
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      };
    }
    xhr.onload = () => {
      if (xhr.status === 413) {
        reject(new Error("Plik jest za duży (limit serwera) — status 413."));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Serwer odpowiedział błędem ${xhr.status}. ${xhr.responseText.slice(0, 200)}`));
        return;
      }
      try {
        const data = JSON.parse(xhr.responseText);
        if (!data.ok) {
          reject(new Error(data.error || "Upload failed"));
          return;
        }
        resolve(data); // { url, filename, mimeType }
      } catch (e) {
        reject(new Error("Nieprawidłowa odpowiedź serwera."));
      }
    };
    xhr.onerror = () => reject(new Error("Błąd sieci podczas wysyłania."));
    xhr.send(toUpload); // File/Blob wysyłany bezpośrednio jako treść zapytania
  });
}

// Czy dwie WIADOMOŚCI OBOK SIEBIE powinny wyglądać jako jedna, zwarta
// "grupka" (jak w Messengerze) — ten sam nadawca, ten sam dzień, i nie
// więcej niż 3 minuty odstępu. "sameSenderFn" porównuje dwie wiadomości i
// mówi, czy to ten sam nadawca — różni się między czatem 1-na-1 (pole
// "sender": "driver"/"admin") a grupowym (pole "driverName").
export function isGroupedWithAdjacent(current, adjacent, sameSenderFn) {
  if (!current || !adjacent) return false;
  if (!sameSenderFn(current, adjacent)) return false;
  if (Math.abs(current.timestamp - adjacent.timestamp) > 3 * 60 * 1000) return false;
  if (new Date(current.timestamp).toDateString() !== new Date(adjacent.timestamp).toDateString()) return false;
  return true;
}

export function formatMessageTime(ts) {
  return new Date(ts).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
}

export function chatDateLabel(ts) {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a, b) => a.toDateString() === b.toDateString();
  if (sameDay(d, today)) return "Dziś";
  if (sameDay(d, yesterday)) return "Wczoraj";
  return d.toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: d.getFullYear() !== today.getFullYear() ? "numeric" : undefined });
}

export function ChatDateSeparator({ timestamp }) {
  return (
    <div className="chat-date-separator">
      <span>{chatDateLabel(timestamp)}</span>
    </div>
  );
}

const LazyTray = lazy(() => import("./attach-up.jsx").then((m) => ({ default: m.UploadTray })));
export function UploadProgressBanner({ progress }) {
  // moduł wysyłania (kilka KB) ładuje się w tle dopiero, gdy czat jest otwarty — start appki go nie pobiera
  useEffect(() => {
    const t = setTimeout(() => import("./attach-up.jsx").catch(() => {}), 2500);
    return () => clearTimeout(t);
  }, []);
  if (progress === null || progress === undefined) return null;
  return (
    <Suspense fallback={null}>
      <LazyTray fallbackProgress={progress} />
    </Suspense>
  );
}

export function uploadFiles(files, onProgress) {
  return import("./attach-up.jsx").then((m) => m.uploadFiles(files, onProgress));
}

export async function uploadVoiceBlob(blob) {
  const type = blob.type || "audio/webm";
  // Wyciągnij prawdziwe rozszerzenie z faktycznego formatu nagrania — telefony
  // z iOS nagrywają w formacie audio/mp4, nie audio/webm jak większość innych
  // przeglądarek. Jeśli plik dostanie złą nazwę (np. .webm dla danych mp4),
  // przeglądarka odtwarzacza nie rozpozna go poprawnie i odtwarzanie się wywali.
  const ext = type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : type.includes("wav") ? "wav" : "webm";
  const file = new File([blob], `glosowka_${Date.now()}.${ext}`, { type });
  // Najpierw usługa, która zamienia nagranie na uniwersalny M4A (działa na każdym telefonie); w razie czego stara droga.
  try {
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), 45000);
    const res = await fetch(`/upload-api/voice?filename=${encodeURIComponent(file.name)}`, { method: "POST", headers: { "Content-Type": type }, body: blob, signal: ctl.signal });
    clearTimeout(timer);
    if (res.ok) {
      const j = await res.json();
      if (j && j.ok && j.url) return { url: j.url, filename: j.filename || file.name, mimeType: j.mimeType || "audio/mp4" };
    }
  } catch (e) {
    /* usługa niedostępna / zła sieć — stara droga niżej */
  }
  return uploadAttachment(file);
}

export async function handleImagePaste(e, setUploading, onSend) {
  const items = e.clipboardData && e.clipboardData.items;
  if (!items) return;
  const imageItems = Array.from(items).filter((it) => it.type && it.type.startsWith("image/"));
  if (!imageItems.length) return;
  e.preventDefault();
  const files = imageItems.map((it) => it.getAsFile()).filter(Boolean);
  if (!files.length) return;
  setUploading(true);
  try {
    const attachment = await uploadFiles(files);
    if (attachment) onSend(attachment);
  } catch (err) {
    alert(`Nie udało się wkleić obrazka: ${err && err.message ? err.message : "nieznany błąd"}`);
  }
  setUploading(false);
}

export function playNotificationPing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(660, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.001, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.25);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.26);
    setTimeout(() => ctx.close(), 400);
  } catch (e) {
    /* dźwięk to tylko dodatek — jeśli się nie uda, appka działa dalej normalnie */
  }
  if (navigator.vibrate) {
    try {
      navigator.vibrate(60);
    } catch (e) {
      /* ignore */
    }
  }
}

export function useNewMessageSound(count) {
  const prevRef = useRef(null);
  useEffect(() => {
    if (prevRef.current !== null && count > prevRef.current) {
      playNotificationPing();
    }
    prevRef.current = count;
  }, [count]);
}

// Animuje liczbę "od zera w górę" do docelowej wartości — używane np. przy
// punktach w rankingu, żeby wejście na tę zakładkę wyglądało bardziej
// efektownie niż zwykłe, statyczne wyświetlenie liczby.
export function useCountUp(target, durationMs = 900) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const from = 0;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out
      setValue(Math.round(from + (target - from) * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line
  }, [target]);
  return value;
}

// Przewijanie rozmowy: "trzymaj się dołu", dopóki użytkownik sam nie przewinie w górę.
//
// Jak to działa (zamiast zgadywania "przewiń teraz, potem za 100/350/700 ms"):
//  * Przy otwarciu rozmowy lista jest przewijana na sam dół ZANIM przeglądarka
//    cokolwiek narysuje (useLayoutEffect) — kierowca od razu widzi najnowszą
//    wiadomość, bez "przeskoku" z góry na dół.
//  * Dopóki użytkownik nie przewinie w górę ("stick"), każda zmiana wysokości
//    listy — załadowane zdjęcie/wideo/głosówka, otwarta klawiatura, zmiana
//    rozmiaru okna — natychmiast (w tej samej klatce) dosuwa widok do dołu.
//  * Nowa wiadomość dosuwa widok PŁYNNIE (animowane przewinięcie).
//  * Jeśli użytkownik przewinie w górę (żeby coś przeczytać), lista NIE ucieka
//    mu spod palca — przycisk "przewiń do najnowszych" pokazuje, że coś doszło.
//  * Gest użytkownika (dotyk/kółko myszy) zawsze ma pierwszeństwo.
// Argument watchDep zostaje w sygnaturze dla zgodności z wywołaniami — zmiany
// zawartości wykrywa obserwator DOM.
export function useSmartAutoScroll(ref, watchDep, resetKey) {
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    let stick = true;
    let ready = false;
    let destroyed = false;
    let raf = 0;
    let ignoreScrollUntil = 0;
    const distanceFromBottom = () => el.scrollHeight - el.scrollTop - el.clientHeight;
    const jump = () => {
      // Własne przewinięcie nie może być wzięte za ruch użytkownika (zdarzenie
      // "scroll" przychodzi z opóźnieniem — treść mogła w tym czasie urosnąć).
      ignoreScrollUntil = Math.max(ignoreScrollUntil, performance.now() + 120);
      el.scrollTop = el.scrollHeight;
    };
    const schedule = () => {
      if (raf || destroyed) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        if (stick && !destroyed) jump();
      });
    };
    const follow = () => {
      if (!stick || destroyed) return;
      if (!ready) {
        schedule();
        return;
      }
      ignoreScrollUntil = performance.now() + 700;
      try {
        el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      } catch (e) {
        jump();
      }
    };
    const onScroll = () => {
      if (performance.now() < ignoreScrollUntil) return;
      stick = distanceFromBottom() < 90;
    };
    const onUserGesture = () => {
      ignoreScrollUntil = 0;
    };
    const onMediaChange = () => schedule();
    const observer = new MutationObserver((mutations) => {
      let addedMessage = false;
      for (const m of mutations) {
        m.addedNodes.forEach((n) => {
          if (n.nodeType === 1 && /chat-bubble|msg-with|chat-date/.test(String(n.className))) addedMessage = true;
        });
      }
      if (addedMessage) follow();
      else schedule();
    });
    el.addEventListener("scroll", onScroll, { passive: true });
    el.addEventListener("touchstart", onUserGesture, { passive: true });
    el.addEventListener("wheel", onUserGesture, { passive: true });
    // Zdarzenia ładowania mediów nie "bąbelkują", ale można je złapać w fazie przechwytywania.
    el.addEventListener("load", onMediaChange, true);
    el.addEventListener("loadedmetadata", onMediaChange, true);
    observer.observe(el, { childList: true, subtree: true });
    let resizeObserver = null;
    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver(schedule);
      resizeObserver.observe(el);
    }
    const vv = window.visualViewport;
    if (vv) vv.addEventListener("resize", schedule);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(schedule).catch(() => {});
    jump();
    // Po chwili "odblokowujemy" animacje wejścia dymków (patrz CSS: data-ready) —
    // wiadomości obecne przy otwarciu pojawiają się od razu, a animują się dopiero
    // te, które przyjdą później.
    const readyTimer = setTimeout(() => {
      ready = true;
      el.setAttribute("data-ready", "1");
    }, 700);
    return () => {
      destroyed = true;
      clearTimeout(readyTimer);
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
      if (resizeObserver) resizeObserver.disconnect();
      if (vv) vv.removeEventListener("resize", schedule);
      el.removeEventListener("scroll", onScroll);
      el.removeEventListener("touchstart", onUserGesture);
      el.removeEventListener("wheel", onUserGesture);
      el.removeEventListener("load", onMediaChange, true);
      el.removeEventListener("loadedmetadata", onMediaChange, true);
      el.removeAttribute("data-ready");
    };
    // eslint-disable-next-line
  }, [resetKey]);
}

export let isAnyRecordingActive = false;

// ---- Bezpieczne przechowanie nagrania (IndexedDB) ----
// Nagranie jest zapisywane NA TELEFONIE zanim ruszy wysyłka (i co kilka sekund w trakcie nagrywania), a kasowane
// dopiero po potwierdzonym wysłaniu wiadomości. Dzięki temu głosówka nie ginie przy braku zasięgu, zamknięciu
// aplikacji czy błędzie zapisu — kierowca może ją wysłać ponownie.
const VOICE_DB = "patex-voice-v1";
function voiceDb() {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(VOICE_DB, 1);
      req.onupgradeneeded = () => req.result.createObjectStore("rec");
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    } catch (e) {
      reject(e);
    }
  });
}
async function voicePut(key, value) {
  try {
    const db = await voiceDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction("rec", "readwrite");
      tx.objectStore("rec").put(value, key);
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) {
    /* brak IndexedDB (tryb prywatny itp.) — nagrywanie działa dalej, tylko bez kopii zapasowej */
  }
}
async function voiceGet(key) {
  try {
    const db = await voiceDb();
    const val = await new Promise((resolve, reject) => {
      const req = db.transaction("rec", "readonly").objectStore("rec").get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return val || null;
  } catch (e) {
    return null;
  }
}
async function voiceDel(key) {
  try {
    const db = await voiceDb();
    await new Promise((resolve) => {
      const tx = db.transaction("rec", "readwrite");
      tx.objectStore("rec").delete(key);
      tx.oncomplete = resolve;
      tx.onerror = resolve;
    });
    db.close();
  } catch (e) {
    /* ignore */
  }
}

// Oczyszczanie dźwięku "w locie" (zanim trafi do nagrania): odcięcie buczenia silnika i szumu wysokich tonów,
// delikatne wyrównanie głośności oraz bramka szumów (cisza między słowami jest wyciszana). Jeśli cokolwiek
// pójdzie nie tak (brak AudioContext, cisza na wyjściu) — nagrywamy surowy dźwięk, jak dotąd.
async function buildVoiceChain(stream, onLevel) {
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  try {
    const ctx = new Ctx();
    if (ctx.state !== "running") {
      // uśpiony kontekst (Android po dłuższym oknie zgody na mikrofon) = strumień wyjściowy NIGDY nie dostanie dźwięku
      try {
        await Promise.race([ctx.resume(), new Promise((r) => setTimeout(r, 700))]);
      } catch (e) {
        /* sprawdzamy stan poniżej */
      }
    }
    if (ctx.state !== "running") {
      try {
        ctx.close();
      } catch (e) {
        /* ignore */
      }
      return null; // nagrywamy surowy mikrofon (bez filtrów) — lepsze niż cisza
    }
    const src = ctx.createMediaStreamSource(stream);
    const hp = ctx.createBiquadFilter();
    hp.type = "highpass";
    hp.frequency.value = 100;
    hp.Q.value = 0.707;
    const lp = ctx.createBiquadFilter();
    lp.type = "lowpass";
    lp.frequency.value = 9500;
    lp.Q.value = 0.707;
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -30;
    comp.knee.value = 24;
    comp.ratio.value = 3;
    comp.attack.value = 0.006;
    comp.release.value = 0.22;
    const makeup = ctx.createGain();
    makeup.gain.value = 1.25;
    const gate = ctx.createGain();
    gate.gain.value = 1;
    const dest = ctx.createMediaStreamDestination();
    const aRaw = ctx.createAnalyser();
    aRaw.fftSize = 1024;
    const aPre = ctx.createAnalyser();
    aPre.fftSize = 1024;
    const aOut = ctx.createAnalyser();
    aOut.fftSize = 1024;
    src.connect(aRaw);
    src.connect(hp);
    hp.connect(lp);
    lp.connect(comp);
    comp.connect(makeup);
    makeup.connect(aPre);
    makeup.connect(gate);
    gate.connect(dest);
    gate.connect(aOut);
    const buf = new Float32Array(1024);
    const rms = (an) => {
      an.getFloatTimeDomainData(buf);
      let s = 0;
      for (let i = 0; i < buf.length; i++) s += buf[i] * buf[i];
      return Math.sqrt(s / buf.length);
    };
    let floor = 0.004;
    let hold = 0;
    let rawMax = 0;
    let outMax = 0;
    let bypassed = false;
    const t0 = Date.now();
    const timer = setInterval(() => {
      const pre = rms(aPre);
      if (pre < floor) floor = floor * 0.85 + pre * 0.15;
      else floor += (pre - floor) * 0.003;
      const open = pre > floor * 2.6 + 0.0035;
      if (open) hold = 8; // ~240 ms "przytrzymania" po słowie
      else if (hold > 0) hold--;
      const g = open || hold > 0 ? 1 : 0.28;
      gate.gain.setTargetAtTime(g, ctx.currentTime, g === 1 ? 0.012 : 0.14);
      if (onLevel) onLevel(Math.min(1, pre * 5));
      rawMax = Math.max(rawMax, rms(aRaw));
      outMax = Math.max(outMax, rms(aOut));
      if (!bypassed) {
        // zabezpieczenie: surowy mikrofon coś "słyszy", a wyjście jest martwe -> pomijamy filtry
        if (Date.now() - t0 > 1800 && rawMax > 0.01 && outMax < 0.0008) {
          bypassed = true;
          try {
            gate.disconnect(dest);
            src.connect(dest);
          } catch (e) {
            /* ignore */
          }
        }
      }
    }, 30);
    return {
      stream: dest.stream,
      // najgłośniejszy poziom, jaki usłyszał surowy mikrofon / wyjście łańcucha (do wykrycia "martwego" nagrania)
      stats: () => ({ raw: rawMax, out: outMax, ticks: Math.round((Date.now() - t0) / 30) }),
      close() {
        clearInterval(timer);
        try {
          ctx.close();
        } catch (e) {
          /* ignore */
        }
      },
    };
  } catch (e) {
    return null;
  }
}

export function useVoiceRecorder(onReady, scope) {
  const storeKey = `rec:${scope || "default"}`;
  const persist = !!scope; // kopię zapasową i panel "niewysłane" mają tylko rozmowy z podanym zakresem (czat kierowcy)
  const vPut = (k, v) => (persist ? voicePut(k, v) : Promise.resolve());
  const vGet = (k) => (persist ? voiceGet(k) : Promise.resolve(null));
  const vDel = (k) => (persist ? voiceDel(k) : Promise.resolve());
  const [isRecording, setIsRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const [pending, setPending] = useState(null); // { seconds, stage: "sending" | "blob" | "uploaded" }
  const [notice, setNotice] = useState(null); // informacja po wysłaniu (np. "była bardzo cicha")
  const autoTriesRef = useRef(0);
  const retryRef = useRef(null);
  // Automatyczne ponawianie: niewysłana głosówka (brak sieci / restart appki) wysyła się sama — po kilku sekundach i po powrocie sieci.
  useEffect(() => {
    if (!persist || !pending || pending.stage === "sending") return undefined;
    const run = () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      if (autoTriesRef.current >= 40) return;
      autoTriesRef.current++;
      if (retryRef.current) retryRef.current();
    };
    const delay = Math.min(6000 + autoTriesRef.current * 3000, 30000);
    const t = setTimeout(run, delay);
    window.addEventListener("online", run);
    return () => {
      clearTimeout(t);
      window.removeEventListener("online", run);
    };
    // eslint-disable-next-line
  }, [pending && pending.stage, persist]);
  const [interrupted, setInterrupted] = useState(false);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chainRef = useRef(null);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  const meterRef = useRef(null);
  const hiddenRef = useRef(false);
  // zawsze NAJNOWSZA funkcja wysyłająca (callback z chwili startu nagrywania bywał "nieaktualny")
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;
  // Osobne "źródło prawdy" o długości nagrania, niezależne od stanu React.
  const secondsRef = useRef(0);

  // nagranie niewysłane przy poprzednim uruchomieniu (zamknięta appka, brak sieci…) — oferujemy ponowną wysyłkę
  useEffect(() => {
    let alive = true;
    vGet(storeKey).then((rec) => {
      if (!alive || !rec || !(rec.blob || rec.attachment)) return;
      setPending({ seconds: rec.seconds || 0, stage: rec.stage === "uploaded" ? "uploaded" : "blob" });
    });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, [storeKey]);

  useEffect(() => {
    const onVis = () => {
      if (document.hidden) {
        if (recorderRef.current && recorderRef.current.state === "recording") hiddenRef.current = true;
      } else if (hiddenRef.current && recorderRef.current && recorderRef.current.state === "recording") {
        setInterrupted(true);
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  async function deliver(attachment, knownSeconds) {
    try {
      const out = await Promise.resolve(onReadyRef.current({ ...attachment, durationSeconds: knownSeconds }));
      return out !== false;
    } catch (e) {
      return false;
    }
  }
  // wysyła zapisane nagranie: plik -> serwer (4 próby), potem wiadomość; kasuje kopię dopiero po sukcesie
  async function processRecord(rec) {
    setPending({ seconds: rec.seconds, stage: "sending" });
    let att = rec.attachment || null;
    for (let attempt = 0; attempt < 4 && !att; attempt++) {
      if (attempt > 0) await new Promise((resolve) => setTimeout(resolve, 800 * attempt));
      try {
        att = await uploadVoiceBlob(rec.blob);
      } catch (e) {
        /* kolejna próba */
      }
    }
    if (!att) {
      if (!persist) {
        setPending(null);
        alert("Nie udało się wysłać głosówki — sprawdź połączenie z internetem i spróbuj nagrać ponownie.");
        return false;
      }
      await vPut(storeKey, { ...rec, stage: "blob" });
      setPending({ seconds: rec.seconds, stage: "blob" });
      return false;
    }
    await vPut(storeKey, { ...rec, attachment: att, stage: "uploaded" });
    if (await deliver(att, rec.seconds)) {
      await vDel(storeKey);
      setPending(null);
      autoTriesRef.current = 0;
      if (rec.quiet) {
        setNotice("Głosówka wysłana, ale była bardzo cicha (niska jakość) — następnym razem mów bliżej mikrofonu.");
        setTimeout(() => setNotice(null), 10000);
      }
      return true;
    }
    setPending({ seconds: rec.seconds, stage: "uploaded" });
    return false;
  }
  async function retry() {
    const rec = await vGet(storeKey);
    if (!rec) {
      setPending(null);
      return;
    }
    await processRecord({ ...rec, seconds: rec.seconds || 0 });
  }
  async function discard() {
    await vDel(storeKey);
    setPending(null);
  }
  retryRef.current = retry;

  function releaseAudio() {
    if (chainRef.current) {
      chainRef.current.close();
      chainRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }

  async function start() {
    // Zabezpieczenie przed przypadkowym podwójnym uruchomieniem.
    if (recorderRef.current && recorderRef.current.state === "recording") return;
    // Niewysłane poprzednie nagranie najpierw trzeba wysłać albo usunąć (inaczej by je nadpisało).
    if (persist && pending && pending.stage !== "sending") return;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    try {
      // Mikrofon najpierw (musi nastąpić od razu po dotknięciu przycisku), potem reszta.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            channelCount: { ideal: 1 },
            sampleRate: { ideal: 48000 },
            echoCancellation: { ideal: true },
            noiseSuppression: { ideal: true },
            autoGainControl: { ideal: true },
          },
        });
      } catch (e) {
        if (e && (e.name === "NotAllowedError" || e.name === "SecurityError")) throw e;
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }
      streamRef.current = stream;
      try {
        if (navigator.wakeLock) wakeLockRef.current = await navigator.wakeLock.request("screen");
      } catch (e) {
        /* brak obsługi wake locka — nagrywanie działa dalej */
      }
      const chain = await buildVoiceChain(stream, (lv) => {
        if (meterRef.current) meterRef.current.style.setProperty("--lv", lv.toFixed(2));
      });
      chainRef.current = chain;
      const recStream = chain ? chain.stream : stream;
      // Jawnie wybrany, sprawdzony format + stała wysoka jakość (128 kb/s).
      // AAC w MP4 gra wszędzie (iPhone, Android, komputery). Samo "audio/mp4" na Androidzie daje Opus-w-MP4, którego Safari nie odtwarza.
      const candidates = ["audio/mp4;codecs=mp4a.40.2", "audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/aac", "audio/ogg"];
      let mr = null;
      for (const c of candidates) {
        if (typeof MediaRecorder.isTypeSupported === "function" && MediaRecorder.isTypeSupported(c)) {
          try {
            mr = new MediaRecorder(recStream, { mimeType: c, audioBitsPerSecond: 128000 });
            break;
          } catch (e) {
            try {
              mr = new MediaRecorder(recStream, { mimeType: c });
              break;
            } catch (e2) {
              /* ta przeglądarka deklaruje obsługę, ale nie potrafi — kolejny format */
            }
          }
        }
      }
      if (!mr) mr = new MediaRecorder(recStream);
      const chunks = [];
      let chunkCount = 0;
      mr.ondataavailable = (e) => {
        if (!e.data || !e.data.size) return;
        chunks.push(e.data);
        chunkCount++;
        // co ~3 s zapisujemy dotychczasowe nagranie na telefonie (odzyskanie po zamknięciu/awarii appki)
        if (chunkCount % 3 === 0) {
          vPut(storeKey, { stage: "recording", blob: new Blob(chunks, { type: mr.mimeType || "audio/webm" }), seconds: secondsRef.current, ts: Date.now() });
        }
      };
      mr.onstop = async () => {
        clearInterval(timerRef.current);
        timerRef.current = null;
        const st = chain && chain.stats ? chain.stats() : null;
        releaseAudio();
        isAnyRecordingActive = false;
        setIsRecording(false);
        // Dokładna, znana długość nagrania (wiarygodniejsza niż metadane pliku).
        const knownDuration = secondsRef.current;
        setSeconds(0);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        // mikrofon prawie nic nie zarejestrował — WYSYŁAMY mimo to (nic nie jest blokowane), a po wysłaniu pokazujemy informację o niskiej jakości
        const quiet = !!(st && st.ticks > 60 && knownDuration >= 2 && Math.max(st.raw, st.out) < 0.002);
        if (blob.size > 300) {
          await processRecord({ blob, seconds: knownDuration, ts: Date.now(), quiet });
        } else {
          await vDel(storeKey);
        }
      };
      recorderRef.current = mr;
      mr.start(1000);
      isAnyRecordingActive = true;
      hiddenRef.current = false;
      setInterrupted(false);
      setIsRecording(true);
      setSeconds(0);
      secondsRef.current = 0;
      timerRef.current = setInterval(() => {
        setSeconds((s) => {
          secondsRef.current = s + 1;
          return s + 1;
        });
      }, 1000);
    } catch (e) {
      releaseAudio();
      /* brak dostępu do mikrofonu — appka po prostu nic nie robi */
    }
  }
  function stop() {
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
  }
  function cancel() {
    if (recorderRef.current) recorderRef.current.onstop = null;
    if (recorderRef.current && recorderRef.current.state !== "inactive") recorderRef.current.stop();
    releaseAudio();
    isAnyRecordingActive = false;
    clearInterval(timerRef.current);
    vDel(storeKey);
    setIsRecording(false);
    setSeconds(0);
    secondsRef.current = 0;
  }
  return { isRecording, seconds, start, stop, cancel, pending, retry, discard, meterRef, interrupted, notice };
}

export const QUICK_REACTIONS = ["👍", "❤️", "😂", "😮", "👏"];

export function MessageReactions({ message, currentUserName, onToggle }) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const reactions = message.reactions || {};
  const entries = Object.entries(reactions).filter(([, users]) => users && users.length > 0);
  return (
    <div className={`msg-reactions ${entries.length > 0 ? "has-reactions" : ""}`}>
      {entries.map(([emoji, users]) => (
        <button
          key={emoji}
          className={`msg-reaction-pill ${users.includes(currentUserName) ? "mine" : ""}`}
          onClick={() => onToggle(emoji)}
        >
          {emoji} {users.length}
        </button>
      ))}
      <button className="msg-reaction-add" onClick={() => setPickerOpen((v) => !v)} aria-label="Dodaj reakcję">
        <Smile size={12} />
      </button>
      {pickerOpen && (
        <div className="msg-reaction-picker">
          {QUICK_REACTIONS.map((e) => (
            <button
              key={e}
              onClick={() => {
                onToggle(e);
                setPickerOpen(false);
              }}
            >
              {e}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Tylko jedna głosówka gra naraz — włączenie kolejnej zatrzymuje poprzednią.
let activeVoiceAudio = null;

// Ozdobna "fala dźwięku": stały, powtarzalny wzór wysokości słupków wyliczony z
// adresu pliku (ta sama głosówka zawsze wygląda tak samo). To tylko wizualizacja
// postępu — nie analizuje prawdziwego dźwięku.
function voiceBarHeights(seedText, count) {
  let h = 2166136261;
  for (let i = 0; i < seedText.length; i++) {
    h ^= seedText.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  const rnd = () => {
    h ^= h << 13;
    h >>>= 0;
    h ^= h >>> 17;
    h ^= h << 5;
    h >>>= 0;
    return (h % 1000) / 1000;
  };
  return Array.from({ length: count }, (_, i) => {
    const envelope = 0.55 + 0.45 * Math.sin((i / (count - 1)) * Math.PI);
    return Math.round(26 + 74 * (0.22 + 0.78 * rnd()) * envelope);
  });
}

function formatVoiceTime(sec) {
  const s = Math.max(0, Math.floor(isFinite(sec) ? sec : 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

const VOICE_RATES = [1, 1.5, 2];

export function AudioWithFallback({ attachment, showBoost }) {
  const [failed, setFailed] = useState(false);
  const [boost, setBoost] = useState(1);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [rate, setRate] = useState(1);
  const audioRef = useRef(null);
  const gainNodeRef = useRef(null);
  const audioGraphSetupRef = useRef(false);
  const audioCtxRef = useRef(null);
  const altRef = useRef(null); // awaryjne odtwarzanie (Web Audio): { ctx, src }
  const [triedPlay, setTriedPlay] = useState(false);
  const [altBusy, setAltBusy] = useState(false);
  const bars = useMemo(() => voiceBarHeights(String(attachment.url || ""), 34), [attachment.url]);

  function setupBoostGraph() {
    if (audioGraphSetupRef.current) return;
    const audio = audioRef.current;
    if (!audio) return;
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      audioCtxRef.current = ctx;
      if (ctx.state !== "running") ctx.resume().catch(() => {});
      const source = ctx.createMediaElementSource(audio);
      const gainNode = ctx.createGain();
      gainNode.gain.value = boost;
      source.connect(gainNode);
      gainNode.connect(ctx.destination);
      gainNodeRef.current = gainNode;
      audioGraphSetupRef.current = true;
      ctx.resume().catch(() => {});
    } catch (e) {
      // jeśli się nie uda (np. przeglądarka nie obsługuje), dźwięk gra po
      // prostu normalnie, na 100%, bez podbicia
    }
  }
  useEffect(() => {
    if (gainNodeRef.current) gainNodeRef.current.gain.value = boost;
  }, [boost]);

  // Płynny postęp fali podczas odtwarzania (odświeżanie co klatkę tylko dla grającej głosówki).
  useEffect(() => {
    if (!playing) return undefined;
    let raf;
    const tick = () => {
      if (audioRef.current) setCurrent(audioRef.current.currentTime);
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing]);
  useEffect(
    () => () => {
      const a = audioRef.current;
      if (a && activeVoiceAudio === a) activeVoiceAudio = null;
    },
    []
  );

  if (failed) {
    return (
      <a href={attachment.url} target="_blank" rel="noreferrer" className="chat-attachment-file">
        <Mic size={13} /> Głosówka — nie można odtworzyć tutaj, pobierz plik
      </a>
    );
  }
  // Jeśli znamy PRAWDZIWĄ długość nagrania (zapisaną w momencie nagrywania,
  // patrz useVoiceRecorder) — używamy jej, gdy przeglądarka nie potrafi
  // odczytać długości z samego pliku (bywa to zawodne przy nagraniach z
  // telefonu, stąd wcześniejsze zgłoszenia "pokazuje 0:00").
  const knownDuration = attachment.durationSeconds;
  const total =
    isFinite(duration) && duration > 0 ? duration : typeof knownDuration === "number" && knownDuration > 0 ? knownDuration : 0;
  const fraction = total > 0 ? Math.min(1, current / total) : 0;

  // Osobna ścieżka dźwięku (Web Audio): pobieramy plik, dekodujemy i gramy — działa, gdy zwykły <audio> milczy.
  async function playAlt() {
    try {
      const a = audioRef.current;
      if (a && !a.paused) a.pause();
      if (altRef.current && altRef.current.src) {
        try {
          altRef.current.src.stop();
        } catch (e) {
          /* już zatrzymane */
        }
        altRef.current.src = null;
        setAltBusy(false);
        return;
      }
      setAltBusy(true);
      const Ctx = window.AudioContext || window.webkitAudioContext;
      const ctx = (altRef.current && altRef.current.ctx) || new Ctx();
      await ctx.resume();
      const buf = await (await fetch(attachment.url)).arrayBuffer();
      const audioBuf = await new Promise((ok, no) => ctx.decodeAudioData(buf, ok, no));
      const node = ctx.createBufferSource();
      node.buffer = audioBuf;
      node.connect(ctx.destination);
      node.onended = () => {
        if (altRef.current) altRef.current.src = null;
        setAltBusy(false);
      };
      altRef.current = { ctx, src: node };
      node.start();
    } catch (e) {
      setAltBusy(false);
      alert("Nie udało się odtworzyć awaryjnie. Sprawdź głośność telefonu i przełącznik wyciszenia (z boku) albo pobierz plik.");
    }
  }
  function toggle() {
    setTriedPlay(true);
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      if (activeVoiceAudio && activeVoiceAudio !== a) activeVoiceAudio.pause();
      activeVoiceAudio = a;
      const p = a.play();
      if (p && p.catch) p.catch(() => {});
    } else {
      a.pause();
    }
  }
  function seekFromEvent(e) {
    const a = audioRef.current;
    if (!a || !(total > 0)) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    try {
      a.currentTime = ratio * total;
      setCurrent(ratio * total);
    } catch (err) {
      /* przeglądarka nie pozwala przewijać tego pliku */
    }
  }
  function cycleRate() {
    const a = audioRef.current;
    const next = VOICE_RATES[(VOICE_RATES.indexOf(rate) + 1) % VOICE_RATES.length];
    setRate(next);
    if (a) a.playbackRate = next;
  }
  return (
    <div className="voice-player-wrap">
      <div className={`voice-player ${playing ? "playing" : ""}`}>
        <audio
          ref={audioRef}
          className="voice-audio-hidden"
          preload="metadata"
          src={attachment.url}
          onPlay={() => {
            setPlaying(true);
          }}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setCurrent(0);
            if (audioRef.current) audioRef.current.currentTime = 0;
          }}
          onError={() => setFailed(true)}
          onDurationChange={(e) => setDuration(e.target.duration)}
          onLoadedMetadata={(e) => {
            const audio = e.target;
            setDuration(audio.duration);
            // Znany błąd przeglądarek z rodziny Chrome: pliki nagrane przez
            // MediaRecorder (webm) czasem dostają błędny/nieskończony czas
            // trwania w metadanych. To wymuszenie "przewinięcia" naprawia
            // wyświetlaną długość — ale TYLKO w Chrome/Chromium. Safari (w tym
            // iPhone) tego problemu nie ma, a ta sama sztuczka potrafi tam
            // wywołać błąd odtwarzania, więc celowo jej tam nie stosujemy.
            const ua = navigator.userAgent || "";
            const isSafari = /^((?!chrome|crios|fxios|android).)*safari/i.test(ua);
            if (!isSafari && !isFinite(audio.duration)) {
              audio.currentTime = 1e101;
              audio.ontimeupdate = () => {
                audio.ontimeupdate = null;
                audio.currentTime = 0;
              };
            }
          }}
        />
        <button
          type="button"
          className="voice-play-btn"
          onClick={toggle}
          aria-label={playing ? "Wstrzymaj głosówkę" : "Odtwórz głosówkę"}
        >
          {playing ? <Pause size={17} fill="currentColor" /> : <Play size={17} fill="currentColor" style={{ marginLeft: 2 }} />}
        </button>
        <div className="voice-body">
          <div
            className="voice-wave"
            role="slider"
            aria-label="Postęp głosówki"
            aria-valuemin={0}
            aria-valuemax={Math.round(total)}
            aria-valuenow={Math.round(current)}
            onPointerDown={(e) => {
              try {
                e.currentTarget.setPointerCapture(e.pointerId);
              } catch (err) {
                /* ignore */
              }
              seekFromEvent(e);
            }}
            onPointerMove={(e) => {
              if (e.buttons === 1) seekFromEvent(e);
            }}
          >
            {bars.map((h, i) => (
              <span
                key={i}
                className={`voice-bar ${(i + 0.5) / bars.length <= fraction ? "on" : ""}`}
                style={{ height: `${h}%`, "--i": i }}
              />
            ))}
          </div>
          <div className="voice-meta">
            <span className="voice-time">
              {playing || current > 0.05 ? `${formatVoiceTime(current)} / ${formatVoiceTime(total)}` : formatVoiceTime(total)}
            </span>
            <button type="button" className="voice-speed-btn" onClick={cycleRate} aria-label="Zmień prędkość odtwarzania">
              {rate}×
            </button>
          </div>
        </div>
      </div>
      {triedPlay && (
        <button type="button" className="voice-alt-btn" onClick={playAlt}>
          {altBusy ? "⏹ Zatrzymaj" : "🔊 Nie słychać? Odtwórz inaczej"}
        </button>
      )}
      {showBoost && (
        <div className="audio-boost-row">
          <Volume2 size={12} />
          {[1, 1.5, 2, 3].map((b) => (
            <button key={b} className={`audio-boost-btn ${boost === b ? "active" : ""}`} onClick={() => {
                setBoost(b);
                if (b > 1) setupBoostGraph(); // graf powstaje dopiero na życzenie (w geście użytkownika), zwykłe odtwarzanie idzie bez niego
                else if (audioCtxRef.current && audioCtxRef.current.state !== "running") audioCtxRef.current.resume().catch(() => {});
              }}>
              {Math.round(b * 100)}%
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// "Equalizer" pokazywany podczas nagrywania głosówki — z miernikiem poziomu mikrofonu na żywo (gdy podano `meter`).
export function RecordingWave({ meter }) {
  return (
    <span className={`rec-wave ${meter ? "live" : ""}`} ref={meter || undefined} aria-hidden="true">
      {Array.from({ length: 16 }, (_, i) => (
        <i
          key={i}
          style={meter ? { "--k": (0.45 + ((i * 7) % 10) / 18).toFixed(2) } : { animationDelay: `${((i * 0.11) % 1).toFixed(2)}s` }}
        />
      ))}
    </span>
  );
}

// Pływający przycisk "przewiń do najnowszych" — pojawia się, gdy ktoś przewinie
// rozmowę w górę, i liczy wiadomości, które przyszły w międzyczasie.
export function ChatScrollFab({ scrollRef }) {
  const [state, setState] = useState({ show: false, count: 0, top: 0 });
  useEffect(() => {
    const el = scrollRef && scrollRef.current;
    if (!el) return undefined;
    let wasNearBottom = true;
    let pending = 0;
    const nearBottom = () => el.scrollHeight - el.scrollTop - el.clientHeight < 140;
    const place = () => {
      const top = el.offsetTop + el.clientHeight - 54;
      setState((s) => (s.top === top ? s : { ...s, top }));
    };
    const onScroll = () => {
      wasNearBottom = nearBottom();
      if (wasNearBottom) pending = 0;
      setState((s) => ({ ...s, show: !wasNearBottom, count: pending }));
      place();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    const mo = new MutationObserver((mutations) => {
      if (el.hasAttribute("data-older")) {
        place();
        return;
      }
      if (!wasNearBottom) {
        for (const m of mutations) {
          m.addedNodes.forEach((n) => {
            if (n.nodeType === 1 && /chat-bubble|msg-with/.test(String(n.className))) pending++;
          });
        }
        setState((s) => ({ ...s, count: pending }));
      }
      place();
    });
    mo.observe(el, { childList: true });
    let ro = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(place);
      ro.observe(el);
    }
    place();
    return () => {
      el.removeEventListener("scroll", onScroll);
      mo.disconnect();
      if (ro) ro.disconnect();
    };
  }, [scrollRef]);
  return (
    <button
      type="button"
      className={`chat-scroll-fab ${state.show ? "visible" : ""}`}
      style={{ top: state.top }}
      aria-label="Przewiń do najnowszych wiadomości"
      tabIndex={state.show ? 0 : -1}
      onClick={() => {
        const el = scrollRef && scrollRef.current;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      }}
    >
      <ChevronDown size={20} />
      {state.count > 0 && <span className="chat-scroll-fab-badge">{state.count > 99 ? "99+" : state.count}</span>}
    </button>
  );
}

// Ptaszki przy ostatniej wiadomości: wysłano / przeczytano.
export function MessageTicks({ read }) {
  return (
    <span className={`msg-ticks ${read ? "read" : ""}`}>
      {read ? <CheckCheck size={13} strokeWidth={2.6} /> : <Check size={13} strokeWidth={2.6} />}
      <span className="msg-ticks-label">{read ? "Przeczytano" : "Wysłano"}</span>
    </span>
  );
}

// Wiadomość składająca się tylko z 1-3 emotikon wyświetlana jest jako duże emoji.
// (Tworzone w try/catch: na bardzo starych przeglądarkach bez obsługi \p{...} ta funkcja po prostu się wyłącza.)
let EMOJI_ONLY_REGEX = null;
try {
  EMOJI_ONLY_REGEX = new RegExp(
    "^(?:\\p{Extended_Pictographic}(?:\\uFE0F|\\u200D\\p{Extended_Pictographic}|\\p{Emoji_Modifier})*\\s*){1,3}$",
    "u"
  );
} catch (e) {
  EMOJI_ONLY_REGEX = null;
}

export const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

export function highlightSearchMatch(text, query) {
  if (!text || !query) return text;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const idx = lower.indexOf(q);
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="chat-search-highlight">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
}

export function linkifyText(text) {
  if (!text) return text;
  if (EMOJI_ONLY_REGEX && EMOJI_ONLY_REGEX.test(String(text).trim())) return <span className="emoji-jumbo">{String(text).trim()}</span>;
  const parts = String(text).split(URL_REGEX);
  return parts.map((part, i) => {
    if (part && URL_REGEX.test(part)) {
      URL_REGEX.lastIndex = 0; // .test() z flagą /g przesuwa wewnętrzny wskaźnik — trzeba go zresetować
      const href = part.startsWith("www.") ? `https://${part}` : part;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: "inherit", textDecoration: "underline", wordBreak: "break-all" }}
        >
          {part}
        </a>
      );
    }
    URL_REGEX.lastIndex = 0;
    return part;
  });
}

export function AttachmentPreview({ attachment, showBoost }) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  if (!attachment) return null;
  const isImage = (attachment.mimeType || "").startsWith("image/");
  const isVideo = (attachment.mimeType || "").startsWith("video/");
  const isAudio = (attachment.mimeType || "").startsWith("audio/");
  if (attachment.type === "album" || Array.isArray(attachment.items) || isImage || isVideo) {
    return <MediaAttachment attachment={attachment} />;
  }
  if (isAudio) {
    return <AudioWithFallback attachment={attachment} showBoost={showBoost} />;
  }
  return (
    <a href={attachment.url} target="_blank" rel="noreferrer" className="chat-attachment-file">
      <Paperclip size={13} /> {attachment.filename}
    </a>
  );
}

export function formatSeenBy(seenBy, allDriverNames) {
  if (!seenBy || seenBy.length === 0) return "";
  return seenBy
    .slice()
    .sort((a, b) => a.seenAt - b.seenAt)
    .map(
      (s) =>
        `${groupDisplayName(s.name, allDriverNames)} (${new Date(s.seenAt).toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" })})`
    )
    .join(", ");
}

export function periodLabel(r) {
  return r.periodTo ? `${r.periodFrom} – ${r.periodTo}` : r.periodFrom;
}

export function todayStr() {
  return new Date().toISOString().slice(0, 10);
}

export function bonusStatus(b) {
  if (b.status === "completed") return "completed";
  if (b.status === "not_achieved") return "not_achieved";
  // Daty mają pierwszeństwo przed ręcznym "Aktywny"/"Zakończony" — bonus z
  // datą "od" w przyszłości jest "nadchodzący" NIEZALEŻNIE od tego, czy
  // ktoś wcześniej kliknął "Aktywny". Wcześniej sprawdzano to w złej
  // kolejności, więc ręczne "Aktywny" potrafiło ukryć bonus, który tak
  // naprawdę jeszcze się nie zaczął.
  const today = todayStr();
  if (b.dateFrom && b.dateFrom > today) return "upcoming";
  if (b.dateTo && b.dateTo < today) return "ended";
  if (b.status === "active") return "active";
  // legacy override field, kept for bonuses saved before the status field existed
  if (b.active === false) return "ended";
  if (b.active === true) return "active";
  return "active";
}

export function bonusDateLabel(b) {
  const from = b.dateFrom || b.createdAt || "";
  return b.dateTo ? `${from} – ${b.dateTo}` : from ? `od ${from} · bezterminowo` : "";
}

export function payoutDecisionKey(r) {
  return `${r.name}|${r.periodFrom}|${r.periodTo}`;
}

export function periodKey(r) {
  return r.periodTo || r.periodFrom || "";
}

export function byPeriodDesc(a, b) {
  return periodKey(b).localeCompare(periodKey(a));
}

export function driverCar(rows, directCar) {
  if (directCar) return directCar;
  const withCar = [...rows].filter((r) => r.carModel).sort(byPeriodDesc);
  return withCar[0] ? withCar[0].carModel : "";
}

export const TASK_COMPLETE_POINTS = 30;

export const RANKING_START_DATE = "2026-07-20"; // zmień tę datę, jeśli kiedyś zechcesz zresetować ranking od nowa

export const COUNTED_STATUSES = ["paid", "transferred"];

export function recordKey(r) {
  return `${r.name.toLowerCase()}|${r.periodFrom}|${r.periodTo}`;
}

export function statusBadge(status) {
  switch (status) {
    case "paid":
      return { text: "Wypłacone", cls: "badge-green", Icon: CheckCircle2 };
    case "settled":
      return { text: "Spłacone", cls: "badge-green", Icon: CheckCircle2 };
    case "debt":
      return { text: "Zaległość", cls: "badge-red", Icon: AlertTriangle };
    case "inprogress":
      return { text: "W trakcie", cls: "badge-amber", Icon: Loader2, spin: true };
    case "notstarted":
      return { text: "Nie rozpoczęto", cls: "badge-amber", Icon: Clock };
    case "transferred":
      return { text: "Przeniesiono", cls: "badge-green", Icon: CalendarClock };
    default:
      return { text: "Oczekuje", cls: "badge-amber", Icon: Clock };
  }
}

export function TopBar({ title, onBack, right }) {
  return (
    <div className="topbar">
      {onBack ? (
        <button className="icon-btn" onClick={onBack} aria-label="Wstecz">
          <ChevronLeft size={20} />
        </button>
      ) : (
        <span className="icon-btn-spacer" />
      )}
      <div className="topbar-title">{title}</div>
      {right || <span className="icon-btn-spacer" />}
    </div>
  );
}
